import { Action, createReducer, on } from '@ngrx/store';
import { EntityAdapter, EntityState } from '@ngrx/entity/src/models';
import { createEntityAdapter } from '@ngrx/entity';

import * as camerasActions from '@live/actions/live-cameras.actions';

import ICamera = Microsoft.VideoIndexer.Live.Contracts.ICamera;
import ILiveCameraAgentJob = Microsoft.VideoIndexer.Live.Contracts.ILiveCameraAgentJob;
import INextPage = Microsoft.VideoIndexer.Contracts.INextPage;

export interface Camera extends ICamera {
  isLoaded?: boolean;
  error?: boolean;
  thumbnailUrl?: string;
  thumbnailError?: boolean;
  isUpdating?: boolean;
}

export interface IState extends EntityState<Camera> {
  loaded: boolean;
  isLoading: boolean;
  error: boolean;
  nextPage: INextPage;
  selectedCameraId: string;
  agentJob?: ILiveCameraAgentJob;
}

function selectCameraId(camera: Camera): string {
  return camera.id;
}

export const adapter: EntityAdapter<Camera> = createEntityAdapter<Camera>({
  selectId: selectCameraId
});

export const initialState: IState = adapter.getInitialState({
  loaded: false,
  isLoading: false,
  error: false,
  nextPage: {
    pageSize: null,
    skip: 0,
    done: false,
    totalCount: null
  },
  selectedCameraId: null,
  agentJob: null
});

const liveCamerasReducer = createReducer(
  initialState,
  on(camerasActions.manageCamerasClicked, state => {
    return {
      ...state,
      error: false,
      loaded: true,
      isLoading: false
    };
  }),
  on(camerasActions.viewCameraSettingsClicked, state => {
    return {
      ...state,
      isLoading: true
    };
  }),
  on(camerasActions.updateCamera, (state, { updatedCameras }) => {
    const updates = updatedCameras.map(id => ({
      id: id.id,
      changes: { isUpdating: true }
    }));
    return adapter.updateMany(updates, state);
  }),
  on(camerasActions.updateCameraBatchSuccess, (state, { updatedCameras }) => {
    const updates = updatedCameras.map(({ id, ...changes }) => ({
      id,
      changes: {
        ...changes,
        isUpdating: false
      }
    }));
    return adapter.updateMany(updates, state);
  }),
  on(camerasActions.updateCameraBatchFailed, (state, { cameras }) => {
    const updates = cameras.map(({ id, ...changes }) => ({
      id,
      changes: {
        ...changes,
        isUpdating: false
      }
    }));
    return adapter.updateMany(updates, state);
  }),
  on(camerasActions.listCamerasStarted, state => {
    return {
      ...state,
      isLoading: true,
      error: false,
      loaded: !!state.nextPage.skip
    };
  }),
  on(camerasActions.listCamerasSucceeded, (state, { cameras, nextPage }) => {
    const loadedCameras = cameras.map(camera => ({ ...camera, isLoaded: true }));
    // We use set all instead of upsert because we want to always clear and use the latest fresh fetched cameras state, never keep old state.
    return adapter.setAll(loadedCameras, {
      ...state,
      isLoading: false,
      loaded: true,
      error: false,
      nextPage
    });
  }),
  on(camerasActions.listCamerasFailed, state => {
    return {
      ...state,
      isLoading: false,
      loaded: true,
      error: true,
      entities: {},
      ids: []
    };
  }),
  on(camerasActions.selectCamera, (state, { cameraId }) => {
    return { ...state, selectedCameraId: cameraId };
  }),
  on(camerasActions.getCameraSucceeded, (state, { camera }) => {
    return adapter.upsertOne({ ...camera, isLoaded: true }, state);
  }),
  on(camerasActions.getCameraFailed, (state, { cameraId }) => {
    return adapter.upsertOne({ id: cameraId, name: undefined, isLoaded: true, error: true }, state);
  }),
  on(camerasActions.clearCameras, state =>
    adapter.removeAll({
      ...state,
      loaded: false,
      isLoading: false,
      nextPage: initialState.nextPage
    })
  ),
  on(camerasActions.getCameraThumbnailSuccess, (state, { cameraId, thumbnailUrl }) => {
    // Only update if the camera is in our collection
    if (!state.entities[cameraId]) {
      return state;
    }

    return adapter.updateOne(
      {
        id: cameraId,
        changes: {
          thumbnailUrl,
          thumbnailError: false
        }
      },
      state
    );
  }),
  on(camerasActions.getCameraThumbnailFailed, (state, { cameraId }) => {
    // Only update if the camera is in our collection
    if (!state.entities[cameraId]) {
      return state;
    }

    return adapter.updateOne(
      {
        id: cameraId,
        changes: {
          thumbnailUrl: null,
          thumbnailError: true
        }
      },
      state
    );
  }),
  on(camerasActions.pinCamera, state => {
    return adapter.updateOne({ id: state.selectedCameraId, changes: { isPinned: true } }, state);
  }),
  on(camerasActions.unpinCamera, state => {
    return adapter.updateOne(
      {
        id: state.selectedCameraId,
        changes: { isPinned: false }
      },
      state
    );
  }),
  on(camerasActions.listCameraAgentJobs, state => {
    return {
      ...state,
      agentJob: null
    };
  }),
  on(camerasActions.listCameraAgentJobsSuccess, (state, { job }) => {
    return {
      ...state,
      agentJob: job
    };
  }),
  on(camerasActions.listCameraAgentJobsFailed, state => {
    return {
      ...state,
      agentJob: null
    };
  })
);

export function reducer(state: IState | undefined, action: Action) {
  return liveCamerasReducer(state, action);
}
